/**
 * Documents3UploaderCompact - Compact document uploader for panel
 *
 * Handles file upload and text paste with entity extraction.
 * Uses documents3 API for graph-only storage.
 */

import { useState, useRef } from 'react'
import { Loader2, Upload, AlertCircle, CheckCircle2, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useDocuments3Processing, type ExtractionProgress } from '@/hooks/useDocuments3Processing'
import { type Document2WithEntities, type ContentType } from '@/lib/api/client'
import { CONTENT_TYPES, CONTENT_TYPE_ICONS } from '@/lib/analyzer/types'
import { extractTextFromPdf } from '@/lib/pdf/extractor'

interface Documents3UploaderCompactProps {
  onComplete?: (doc: Document2WithEntities) => void
  onError?: (error: Error) => void
}

export function Documents3UploaderCompact({
  onComplete,
  onError,
}: Documents3UploaderCompactProps) {
  const [activeTab, setActiveTab] = useState<'paste' | 'upload'>('paste')
  const [title, setTitle] = useState('')
  const [contentType, setContentType] = useState<ContentType>('custom')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isReading, setIsReading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    uploadDocument,
    pasteContent,
    extractAndStore,
    extractionProgress,
    isConfigured,
    error,
    clearError,
  } = useDocuments3Processing({
    autoLoadDocuments: false,
    onError,
  })

  const isProcessing = extractionProgress.stage !== 'idle' && extractionProgress.stage !== 'complete' && extractionProgress.stage !== 'error'

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    clearError()
    setIsReading(true)

    try {
      const isPdf = selectedFile.name.toLowerCase().endsWith('.pdf')

      if (isPdf) {
        const extracted = await extractTextFromPdf(selectedFile)
        setContent(extracted.fullText)
      } else {
        const text = await selectedFile.text()
        setContent(text)
      }

      if (!title) {
        setTitle(selectedFile.name.replace(/\.[^.]+$/, ''))
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Failed to read file'))
    } finally {
      setIsReading(false)
    }
  }

  const handleSubmit = async () => {
    if (!title.trim() || content.length < 50) return

    try {
      clearError()

      // Create document
      let doc: Document2WithEntities
      if (activeTab === 'upload' && file) {
        doc = await uploadDocument(file, contentType, title.trim())
      } else {
        doc = await pasteContent(title.trim(), content, contentType)
      }

      // Extract entities and store in graph
      await extractAndStore(doc.document.id)

      // Reset form
      setTitle('')
      setContent('')
      setFile(null)

      onComplete?.(doc)
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Upload failed'))
    }
  }

  const canSubmit = title.trim() && content.length >= 50 && isConfigured && !isProcessing

  return (
    <div className="space-y-3">
      {/* Title */}
      <div className="space-y-1">
        <Label htmlFor="title" className="text-xs">Title</Label>
        <Input
          id="title"
          placeholder="Document title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-8 text-sm"
          disabled={isProcessing}
        />
      </div>

      {/* Content Type */}
      <div className="space-y-1">
        <Label className="text-xs">Content Type</Label>
        <Select
          value={contentType}
          onValueChange={(v) => setContentType(v as ContentType)}
          disabled={isProcessing}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONTENT_TYPES.map((ct) => {
              const Icon = CONTENT_TYPE_ICONS[ct.icon]
              return (
                <SelectItem key={ct.id} value={ct.id}>
                  <span className="flex items-center gap-2 text-sm">
                    {Icon && <Icon className="h-3.5 w-3.5 text-text-muted" />}
                    {ct.name}
                  </span>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Content Input */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'paste' | 'upload')}>
        <TabsList className="grid w-full grid-cols-2 h-8">
          <TabsTrigger value="paste" className="text-xs" disabled={isProcessing}>
            Paste
          </TabsTrigger>
          <TabsTrigger value="upload" className="text-xs" disabled={isProcessing}>
            Upload
          </TabsTrigger>
        </TabsList>

        <TabsContent value="paste" className="space-y-1 mt-2">
          <Textarea
            placeholder="Paste document content..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            className="text-sm font-mono resize-none"
            disabled={isProcessing}
          />
          <p className="text-xs text-text-muted">
            {content.length} chars
            {content.length > 0 && content.length < 50 && (
              <span className="text-red-500"> (min 50)</span>
            )}
          </p>
        </TabsContent>

        <TabsContent value="upload" className="space-y-2 mt-2">
          <div
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              isProcessing ? 'opacity-50 cursor-not-allowed' : 'border-warm-300 hover:border-solution'
            }`}
            onClick={() => !isProcessing && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md,.markdown,.pdf"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isProcessing}
            />
            {isReading ? (
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-solution" />
            ) : file ? (
              <div>
                <p className="font-medium text-text-primary text-sm">{file.name}</p>
                <p className="text-xs text-text-muted">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div>
                <Upload className="h-5 w-5 mx-auto text-text-muted mb-1" />
                <p className="text-xs text-text-secondary">PDF, TXT, MD</p>
              </div>
            )}
          </div>
          {content && (
            <p className="text-xs text-text-muted">{content.length} chars loaded</p>
          )}
        </TabsContent>
      </Tabs>

      {/* Progress */}
      <ExtractionProgressDisplay progress={extractionProgress} />

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-700 p-2 rounded text-xs flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {error.message || String(error) || 'An error occurred'}
        </div>
      )}

      {/* API Key Warning */}
      {!isConfigured && (
        <div className="bg-amber-50 text-amber-700 p-2 rounded text-xs flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5" />
          Configure API key in Settings
        </div>
      )}

      {/* Submit */}
      <Button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full bg-solution hover:bg-solution/90 h-8 text-sm"
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <Zap className="h-3.5 w-3.5 mr-2" />
            Extract Entities
          </>
        )}
      </Button>
    </div>
  )
}

// ============================================================================
// PROGRESS DISPLAY
// ============================================================================

function ExtractionProgressDisplay({ progress }: { progress: ExtractionProgress }) {
  if (progress.stage === 'idle') return null

  const stages = [
    { key: 'analyzing', label: 'Analyzing' },
    { key: 'storing', label: 'Storing' },
    { key: 'complete', label: 'Complete' },
  ]

  return (
    <div className="bg-warm-50 border border-warm-200 rounded p-2">
      <div className="flex items-center gap-2 mb-2">
        {progress.stage === 'complete' ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
        ) : progress.stage === 'error' ? (
          <AlertCircle className="h-3.5 w-3.5 text-red-500" />
        ) : (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-solution" />
        )}
        <span className="text-xs font-medium text-text-primary">
          {progress.stage === 'analyzing' && 'Extracting entities...'}
          {progress.stage === 'storing' && 'Storing in graph...'}
          {progress.stage === 'complete' && 'Complete'}
          {progress.stage === 'error' && 'Error'}
        </span>
      </div>

      {(progress.stage === 'storing' || progress.stage === 'complete') && (
        <div className="text-xs text-text-secondary">
          {progress.entitiesFound} entities, {progress.relationshipsFound} relationships
        </div>
      )}

      {progress.metadata && (
        <div className="text-xs text-text-muted mt-1">
          {progress.metadata.provider}/{progress.metadata.model} · {(progress.metadata.durationMs / 1000).toFixed(1)}s
        </div>
      )}

      {progress.error && (
        <div className="text-xs text-red-600 mt-1">{progress.error}</div>
      )}
    </div>
  )
}
